import { useDocumentVisibility } from "ahooks"
import { useCallback, useEffect, useState } from "react"
import { TQR_CODE_CONFIG } from "../utils/config"
import {
  getTQRCodeElapsedAndRemaining,
  createQRCodeChunks,
  createTQRCodeData,
  extractUUIDFromQRCodeURL,
  getCurrentTimeWithFallback,
} from "../utils/utils"
import getPublicGuest from "../utils/api"

interface UseTQRCodeDataParams {
  qrCodeUrl: string
  guestId: string
  initialCheckedIn?: boolean
}

export type TQRCodeData = { id: string; exp: number }

interface UseTQRCodeDataReturn {
  isLoading: boolean
  tqrCodeData: TQRCodeData | null
  remainingTime: number
  elapsedTime: number
  isCheckedIn: boolean
  currentTQRCodeValue: string
}

export const useTQRCodeData = ({
  qrCodeUrl,
  guestId,
  initialCheckedIn,
}: UseTQRCodeDataParams): UseTQRCodeDataReturn => {
  const documentVisibility = useDocumentVisibility()
  const extractedUUID = extractUUIDFromQRCodeURL(qrCodeUrl)

  const [isCheckedIn, setIsCheckedIn] = useState<boolean>(
    initialCheckedIn ?? false,
  )
  const [isLoading, setIsLoading] = useState(true)
  const [serverTime, setServerTime] = useState<number | null>(null)
  const [tqrCodeData, setTQRCodeData] = useState<TQRCodeData | null>(null)
  const [tqrCodeChunks, setTQRCodeChunks] = useState<string[]>([])
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0)
  const [remainingTime, setRemainingTime] = useState<number>(
    TQR_CODE_CONFIG.DURATION,
  )
  const [elapsedTime, setElapsedTime] = useState<number>(0)
  const [tqrCodeStartTime, setTQRCodeStartTime] = useState<number | null>(null)

  const currentTQRCodeValue = tqrCodeChunks[currentChunkIndex] || ""

  const checkGuestStatus = useCallback(async () => {
    try {
      const updatedGuest = await getPublicGuest(guestId)
      if (updatedGuest?.checkedIn) {
        setIsCheckedIn(true)
      }
    } catch {
      console.error("Failed to check guest status.")
    }
  }, [guestId])

  const refreshTQRCode = useCallback(async () => {
    if (!extractedUUID) return

    const newTime = await getCurrentTimeWithFallback()
    const newTQRCodeData = createTQRCodeData(extractedUUID, newTime)

    setTQRCodeData(newTQRCodeData)
    setTQRCodeStartTime(newTime)
    setElapsedTime(0)
    setRemainingTime(TQR_CODE_CONFIG.DURATION)

    // We're periodically checking if guest has checked in so that we stop further
    // generation of QR Codes and display proper UI if that's the case
    checkGuestStatus()
  }, [extractedUUID, isCheckedIn])

  useEffect(() => {
    if (tqrCodeData) {
      const chunks = createQRCodeChunks(tqrCodeData, guestId)
      setTQRCodeChunks(chunks)
    }
  }, [tqrCodeData, guestId])

  useEffect(() => {
    if (isLoading || !serverTime || isCheckedIn || tqrCodeChunks.length === 0) {
      return () => {}
    }

    const rotationInterval = setInterval(() => {
      setCurrentChunkIndex(
        (prevIndex) => (prevIndex + 1) % tqrCodeChunks.length,
      )
    }, TQR_CODE_CONFIG.ROTATION_INTERVAL_DURATION * 1000)

    return () => {
      clearInterval(rotationInterval)
    }
  }, [tqrCodeChunks, isLoading, serverTime, isCheckedIn])

  useEffect(() => {
    const fetchInitialTime = async () => {
      try {
        const now = await getCurrentTimeWithFallback()
        setServerTime(now)

        if (extractedUUID) {
          const tqrData = createTQRCodeData(extractedUUID, now)
          setTQRCodeData(tqrData)
          setTQRCodeStartTime(now)
        }

        setIsLoading(false)
      } catch {
        const now = Date.now()
        setServerTime(now)

        if (extractedUUID) {
          const tqrData = createTQRCodeData(extractedUUID, now)
          setTQRCodeData(tqrData)
          setTQRCodeStartTime(now)
        }

        setIsLoading(false)
      }
    }

    fetchInitialTime()
  }, [extractedUUID])

  useEffect(() => {
    if (isLoading || !tqrCodeStartTime || isCheckedIn) {
      return () => {}
    }

    const interval = setInterval(() => {
      const { elapsed, remaining } = getTQRCodeElapsedAndRemaining(
        tqrCodeStartTime,
        TQR_CODE_CONFIG.DURATION,
      )
      setElapsedTime(elapsed)
      setRemainingTime(remaining)

      if (remaining === 0) {
        refreshTQRCode()
      }
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [isLoading, tqrCodeStartTime, isCheckedIn, refreshTQRCode])

  useEffect(() => {
    if (isLoading || isCheckedIn || documentVisibility !== "visible") return

    refreshTQRCode()
  }, [documentVisibility, isLoading, isCheckedIn, refreshTQRCode])

  return {
    isLoading,
    tqrCodeData,
    remainingTime,
    elapsedTime,
    isCheckedIn,
    currentTQRCodeValue,
  }
}

export default useTQRCodeData
